import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import {
  GET_ME,
  UPDATE_WORKLIST_SETTINGS,
  UPDATE_CUSTOM_WORKLIST,
  GET_CUSTOM_WORKLISTS,
} from 'modules/Apollo/queries';
import type {
  WorklistColumn,
  UpdateWorklistSettingsMutation,
  UpdateWorklistSettingsMutationVariables,
  UpdateCustomWorklistMutation,
  UpdateCustomWorklistMutationVariables,
  GetMeQuery,
  GetMeQueryVariables,
  GetCustomWorklistsQuery,
  GetCustomWorklistsQueryVariables,
  SortColumn,
  SortOrder,
  WorklistPreferences,
  WorklistPreferencesInput,
  CustomWorklist,
  WorklistSettings,
  PrivateWorklistPreferences,
} from 'generated/graphql';
import { WorklistPreferenceType } from 'generated/graphql';
import { convertStateToWorklistFilters } from '../../WorklistFilters/utils';
import { getWorklistKey } from '../utils/worklistUtils';
import { convertTableSortingToWorklistSorting } from './useCustomWorklistPreferences';
import { useCustomWorklists } from './useCustomWorklists';
import { FilterStateType } from '../../WorklistFilters/types';
import { useIsFilterPersistenceDisabled } from './useIsFilterPersistenceDisabled';
import { WithApolloContext } from 'modules/Apollo/types';

// Helper to convert WorklistPreferences output type to input type
const convertPreferenceToInput = (pref: WorklistPreferences): WorklistPreferencesInput => ({
  worklistKey: pref.worklistKey,
  type: pref.type,
  customWorklistIds: [...pref.customWorklistIds],
  columns: pref.columns.map((col) => ({
    id: col.id,
    enabled: col.enabled,
    width: col.width,
    pinPosition: col.pinPosition ?? undefined,
    order: col.order ?? undefined,
  })),
  sorting: pref.sorting.map((sort) =>
    sort
      ? {
          column: sort.column,
          desc: sort.desc,
        }
      : null
  ),
  filters:
    pref.filters?.map((filter) => ({
      name: filter.name,
      values: [...filter.values],
    })) ?? [],
});

const mergeColumnsWithExisting = (
  updatedColumns: ReadonlyArray<WorklistColumn>,
  existingColumns: ReadonlyArray<WorklistColumn>
): Array<WorklistColumn> => {
  if (existingColumns.length === 0) {
    return [...updatedColumns];
  }

  const existingColumnMap = new Map(existingColumns.map((col) => [col.id, col]));
  const merged = updatedColumns.map((col) => {
    const existing = existingColumnMap.get(col.id);
    if (!existing) {
      return col;
    }

    return {
      ...col,
      pinPosition: col.pinPosition ?? existing.pinPosition,
      order: col.order ?? existing.order,
      width: col.width ?? existing.width,
      enabled: col.enabled ?? existing.enabled,
    };
  });

  const mergedIds = new Set(merged.map((col) => col.id));
  existingColumns.forEach((col) => {
    if (!mergedIds.has(col.id)) {
      merged.push(col);
    }
  });

  return merged;
};

interface UseCustomWorklistPreferenceUpdatesProps {
  activeCustomWorklistIDs?: string[];
  privateWorklists: Map<string, CustomWorklist>;
  worklistSettings?: WorklistSettings;
  customWorklistSettings?: WorklistPreferences | PrivateWorklistPreferences | null;
  userSmid?: string;
  skipQueries?: boolean;
}

export const useCustomWorklistPreferenceUpdates = ({
  activeCustomWorklistIDs,
  privateWorklists,
  worklistSettings,
  customWorklistSettings,
  userSmid,
  skipQueries = false,
  context,
}: UseCustomWorklistPreferenceUpdatesProps & WithApolloContext) => {
  const latestColumnsRef = useRef<Array<WorklistColumn> | null>(null);

  const [updateWorklistSettingsMutation] = useMutation<
    UpdateWorklistSettingsMutation,
    UpdateWorklistSettingsMutationVariables
  >(UPDATE_WORKLIST_SETTINGS, { context });

  const [updateCustomWorklistMutation] = useMutation<
    UpdateCustomWorklistMutation,
    UpdateCustomWorklistMutationVariables
  >(UPDATE_CUSTOM_WORKLIST, { context });

  const { hasPrivateWorklist } = useCustomWorklists({
    queryOptions: { context, skip: skipQueries },
  });

  const isStickyFiltersDisabled = useIsFilterPersistenceDisabled({
    context,
    skip: skipQueries,
  });

  useEffect(() => {
    if (customWorklistSettings?.columns && customWorklistSettings.columns.length > 0) {
      latestColumnsRef.current = [...customWorklistSettings.columns];
    }
  }, [customWorklistSettings?.columns]);

  const getColumnsForPreferenceUpdate = useCallback(() => {
    if (customWorklistSettings?.columns && customWorklistSettings.columns.length > 0) {
      return [...customWorklistSettings.columns];
    }

    if (latestColumnsRef.current && latestColumnsRef.current.length > 0) {
      return [...latestColumnsRef.current];
    }

    return [];
  }, [customWorklistSettings?.columns]);

  const updatePublicWorklistPreferences = useCallback(
    async (
      updatedColumns: Array<WorklistColumn>,
      updatedSortColumns?: SortColumn[],
      updatedSortOrders?: SortOrder[],
      updatedFilters?: FilterStateType
    ) => {
      if (!activeCustomWorklistIDs?.[0]) {
        return;
      }

      const isCombinedWorklist = activeCustomWorklistIDs.length > 1;
      const worklistKey = isCombinedWorklist
        ? getWorklistKey(activeCustomWorklistIDs) // Consistent key for combined worklists
        : activeCustomWorklistIDs[0];

      const existingPreferences = worklistSettings?.worklistPreferences ?? [];
      const index = existingPreferences.findIndex(
        (pref: WorklistPreferences) => pref.worklistKey === worklistKey
      );
      const existingPreference = index === -1 ? null : existingPreferences[index];
      const existingPreferenceColumns = existingPreference?.columns ?? [];

      // Convert table sorting to worklistPreferences sorting format if provided
      const updatedSorting =
        updatedSortColumns && updatedSortOrders
          ? convertTableSortingToWorklistSorting(updatedSortColumns, updatedSortOrders)
          : (customWorklistSettings?.sorting ?? existingPreference?.sorting ?? []);

      // Convert filter state to worklistPreferences filter format if provided
      const updatedFiltersArray = updatedFilters
        ? convertStateToWorklistFilters(updatedFilters)
        : (customWorklistSettings?.filters ?? existingPreference?.filters ?? []);

      const mergedColumns = mergeColumnsWithExisting(updatedColumns, existingPreferenceColumns);

      const newPreferenceInput: WorklistPreferencesInput = {
        worklistKey,
        type: isCombinedWorklist
          ? WorklistPreferenceType.CombinedWorklists
          : WorklistPreferenceType.PublicWorklist,
        customWorklistIds: activeCustomWorklistIDs,
        columns: mergedColumns.map((col) => ({
          id: col.id,
          enabled: col.enabled,
          width: col.width,
          pinPosition: col.pinPosition ?? undefined,
          order: col.order ?? undefined,
        })),
        sorting: updatedSorting.map((sort) =>
          sort
            ? {
                column: sort.column,
                desc: sort.desc,
              }
            : null
        ),
        filters: updatedFiltersArray,
      };

      // Convert existing preferences to input types
      const existingPreferencesInput = existingPreferences.map((pref) =>
        convertPreferenceToInput(pref)
      );

      const preferencesInput =
        index === -1
          ? [...existingPreferencesInput, newPreferenceInput]
          : [
              ...existingPreferencesInput.slice(0, index),
              newPreferenceInput,
              ...existingPreferencesInput.slice(index + 1),
            ];

      // optimistic response with output types
      const newPreferenceOutput: WorklistPreferences = {
        __typename: 'WorklistPreferences',
        worklistKey,
        type: isCombinedWorklist
          ? WorklistPreferenceType.CombinedWorklists
          : WorklistPreferenceType.PublicWorklist,
        customWorklistIds: activeCustomWorklistIDs,
        columns: mergedColumns,
        sorting: updatedSorting,
        filters: updatedFiltersArray.map((filter) => ({
          __typename: 'FilterDefinition',
          name: filter.name ?? '',
          values: filter.values ?? [],
        })),
      };

      const updatedPreferencesOutput =
        index === -1
          ? [...existingPreferences, newPreferenceOutput]
          : [
              ...existingPreferences.slice(0, index),
              newPreferenceOutput,
              ...existingPreferences.slice(index + 1),
            ];

      await updateWorklistSettingsMutation({
        variables: {
          worklistPreferences: preferencesInput,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateWorklistSettings: {
            ...worklistSettings,
            __typename: 'WorklistSettings',
            worklistPreferences: updatedPreferencesOutput,
          },
        },
        update: (proxy) => {
          const data = proxy.readQuery<GetMeQuery, GetMeQueryVariables>({ query: GET_ME });

          if (data == null) return;
          proxy.writeQuery({
            query: GET_ME,
            data: {
              me: {
                ...data?.me,
                worklistSettings: {
                  ...data?.me?.worklistSettings,
                  worklistPreferences: updatedPreferencesOutput,
                },
              },
            },
          });
        },
      });
    },
    [
      activeCustomWorklistIDs,
      worklistSettings,
      customWorklistSettings,
      updateWorklistSettingsMutation,
    ]
  );

  const updatePrivateWorklistPreferences = useCallback(
    async (
      updatedColumns: Array<WorklistColumn>,
      updatedSortColumns?: SortColumn[],
      updatedSortOrders?: SortOrder[],
      updatedFilters?: FilterStateType
    ) => {
      if (!activeCustomWorklistIDs?.[0] || !userSmid) return;

      const currentWorklist = privateWorklists.get(activeCustomWorklistIDs[0]);
      if (!currentWorklist?.advancedSettings) return;
      const existingPreferences = currentWorklist.advancedSettings?.worklistPreferences;

      // Convert table sorting to worklistPreferences sorting format if provided
      const updatedSorting =
        updatedSortColumns && updatedSortOrders
          ? convertTableSortingToWorklistSorting(updatedSortColumns, updatedSortOrders)
          : (customWorklistSettings?.sorting ?? existingPreferences?.sorting ?? []);

      const updatedFiltersArray = updatedFilters
        ? convertStateToWorklistFilters(updatedFilters)
        : (customWorklistSettings?.filters ?? existingPreferences?.filters ?? []);

      const existingColumns = existingPreferences?.columns ?? [];
      const mergedColumns = mergeColumnsWithExisting(updatedColumns, existingColumns);

      await updateCustomWorklistMutation({
        variables: {
          smid: activeCustomWorklistIDs[0],
          advancedSettings: {
            ...currentWorklist.advancedSettings,
            worklistPreferences: {
              columns: mergedColumns,
              sorting: updatedSorting,
              filters: updatedFiltersArray,
            },
          },
          internalSettings: currentWorklist.internalSettings,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCustomWorklist: {
            ...currentWorklist,
            __typename: 'CustomWorklistDetail',
            filters: [],
            advancedSettings: {
              ...currentWorklist.advancedSettings,
              __typename: 'CustomWorklistAdvancedSettings',
              worklistPreferences: {
                __typename: 'PrivateWorklistPreferences',
                columns: mergedColumns,
                sorting: updatedSorting,
                filters: updatedFiltersArray,
              },
            },
            internalSettings: currentWorklist.internalSettings
              ? {
                  ...currentWorklist.internalSettings,
                  __typename: 'CustomWorklistInternalSettings',
                }
              : undefined,
          },
        },
        update: (proxy) => {
          const customWorklistsData = proxy.readQuery<
            GetCustomWorklistsQuery,
            GetCustomWorklistsQueryVariables
          >({
            query: GET_CUSTOM_WORKLISTS,
            variables: { userSmid },
          });

          if (customWorklistsData?.customWorklists?.privateWorklists) {
            const updatedPrivateWorklists =
              customWorklistsData.customWorklists.privateWorklists.map((worklist) => {
                if (worklist.smid === activeCustomWorklistIDs[0]) {
                  return {
                    ...worklist,
                    advancedSettings: {
                      ...worklist.advancedSettings,
                      worklistPreferences: {
                        ...worklist.advancedSettings?.worklistPreferences,
                        columns: mergedColumns,
                        sorting: updatedSorting,
                        filters: updatedFiltersArray,
                      },
                    },
                  };
                }
                return worklist;
              });

            proxy.writeQuery({
              query: GET_CUSTOM_WORKLISTS,
              variables: { userSmid },
              data: {
                ...customWorklistsData,
                customWorklists: {
                  ...customWorklistsData.customWorklists,
                  privateWorklists: updatedPrivateWorklists,
                },
              },
            });
          }
        },
      });
    },
    [
      activeCustomWorklistIDs,
      privateWorklists,
      customWorklistSettings,
      updateCustomWorklistMutation,
      userSmid,
    ]
  );

  const updateCustomWorklistColumnSettings = useCallback(
    async (updatedColumns: Array<WorklistColumn>) => {
      if (!activeCustomWorklistIDs?.[0]) {
        return;
      }

      latestColumnsRef.current = [...updatedColumns];

      // For combined worklists or single public worklists, use the new worklistPreferences
      // Only use private preferences if it's a single private worklist
      const isSinglePrivateWorklist =
        activeCustomWorklistIDs.length === 1 && hasPrivateWorklist(activeCustomWorklistIDs);

      if (isSinglePrivateWorklist) {
        await updatePrivateWorklistPreferences(updatedColumns);
      } else {
        await updatePublicWorklistPreferences(updatedColumns);
      }
    },
    [
      activeCustomWorklistIDs,
      hasPrivateWorklist,
      updatePrivateWorklistPreferences,
      updatePublicWorklistPreferences,
    ]
  );

  const updateCustomWorklistSortingSettings = useCallback(
    async (sortColumns: SortColumn[], sortOrders: SortOrder[]) => {
      if (!activeCustomWorklistIDs?.[0]) return;

      const columnsToSave = getColumnsForPreferenceUpdate();
      if (columnsToSave.length === 0) return;

      const isSinglePrivateWorklist =
        activeCustomWorklistIDs.length === 1 && hasPrivateWorklist(activeCustomWorklistIDs);

      if (isSinglePrivateWorklist) {
        await updatePrivateWorklistPreferences(columnsToSave, sortColumns, sortOrders);
      } else {
        await updatePublicWorklistPreferences(columnsToSave, sortColumns, sortOrders);
      }
    },
    [
      activeCustomWorklistIDs,
      hasPrivateWorklist,
      getColumnsForPreferenceUpdate,
      updatePrivateWorklistPreferences,
      updatePublicWorklistPreferences,
    ]
  );

  const updateCustomWorklistFilterSettings = useCallback(
    async (filters: FilterStateType) => {
      if (isStickyFiltersDisabled) return;
      if (!activeCustomWorklistIDs?.[0]) return;

      const columnsToSave = getColumnsForPreferenceUpdate();
      if (columnsToSave.length === 0) return;

      const isSinglePrivateWorklist =
        activeCustomWorklistIDs.length === 1 && hasPrivateWorklist(activeCustomWorklistIDs);

      if (isSinglePrivateWorklist) {
        await updatePrivateWorklistPreferences(columnsToSave, undefined, undefined, filters);
      } else {
        await updatePublicWorklistPreferences(columnsToSave, undefined, undefined, filters);
      }
    },
    [
      isStickyFiltersDisabled,
      activeCustomWorklistIDs,
      hasPrivateWorklist,
      getColumnsForPreferenceUpdate,
      updatePrivateWorklistPreferences,
      updatePublicWorklistPreferences,
    ]
  );

  return {
    updateCustomWorklistColumnSettings,
    updateCustomWorklistSortingSettings,
    updateCustomWorklistFilterSettings,
    updatePublicWorklistPreferences,
    updatePrivateWorklistPreferences,
  };
};
